// T13 — bölüm metni ↔ KPI tablosu sayı tutarlılığı uyarı kutusu (Review sağ/orta panel).
// Non-blocking: yalnız bilgi; satıra tıklanınca editördeki ifadeye kaydırılır (onLocate).
// P2-7 (gerçek LLM turu): uyarı başına "yoksay" — yerel (localStorage, proje+bölüm anahtarı),
// başlıkta toplam sayı + yoksayılan sayısı + geri getir.
// TODO-KPI-27: `kind:"unitNote"` satırları (birim dönüşümüyle tabloya eşdeğer sayı — 800.596 MWh ↔
// 2.882.145,6 GJ) uyarı SAYILMAZ; kutunun altında nötr "Birim farkı" bilgi satırı olarak listelenir.
import { useEffect, useMemo, useState } from "react";
import type { KpiTextWarning } from "@mahir/shared";
import { formatKpiNumber } from "@mahir/shared";
import {
  KPI_WARNINGS_COPY as C,
  kpiWarningKey,
  loadIgnoredKeys,
  saveIgnoredKeys,
  splitKpiWarnings,
  splitUnitNotes,
} from "../../lib/kpi-warnings-ignore";

interface Props {
  warnings: KpiTextWarning[];
  onLocate?: ((w: KpiTextWarning) => void) | undefined;
  /** localStorage anahtarı (proje+bölüm); yoksa "yoksay" bellek içi kalır. */
  storageKey?: string | undefined;
}

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function KpiWarningsBox({ warnings: all, onLocate, storageKey }: Props): JSX.Element | null {
  const [ignored, setIgnored] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setIgnored(storageKey ? loadIgnoredKeys(storage(), storageKey) : new Set());
  }, [storageKey]);
  const { warnings, unitNotes } = useMemo(() => splitUnitNotes(all), [all]);
  const { visible, ignoredCount } = useMemo(
    () => splitKpiWarnings(warnings, ignored),
    [warnings, ignored],
  );
  if (all.length === 0) return null;
  if (warnings.length === 0) return <UnitNotesOnly notes={unitNotes} onLocate={onLocate} />;

  const update = (next: Set<string>): void => {
    setIgnored(next);
    if (storageKey) saveIgnoredKeys(storage(), storageKey, next);
  };
  const ignoreOne = (w: KpiTextWarning): void => update(new Set([...ignored, kpiWarningKey(w)]));
  const restore = (): void => update(new Set());

  return (
    <div
      className="mb-3 rounded-md p-3 text-sm"
      role="status"
      style={{
        background: "color-mix(in srgb, var(--color-warning) 10%, white)",
        border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
        color: "var(--color-text)",
      }}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <strong style={{ color: "var(--color-warning)" }}>⚠ {C.title}</strong>
        <span className="text-xs" style={{ color: "var(--color-text-2)" }}>
          {C.summary(visible.length, ignoredCount)}
        </span>
        {ignoredCount > 0 && (
          <button
            type="button"
            className="text-xs underline"
            style={{ color: "var(--color-text-2)" }}
            onClick={restore}
          >
            {C.restore}
          </button>
        )}
      </div>
      {visible.length === 0 ? (
        <p className="mt-1 text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.allIgnored}
        </p>
      ) : (
        <>
          <p className="mt-1">{C.lead}</p>
          <ul className="mt-2 flex flex-col gap-1">
            {visible.slice(0, 12).map((w, i) => (
              <li
                key={`${w.indicatorCode}-${w.sectionIndex}-${i}`}
                className="flex items-start gap-2"
              >
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => onLocate?.(w)}
                  title="Metinde göster"
                >
                  <span className="font-medium">{w.indicatorLabel}</span>
                  {": metinde "}
                  <span style={{ fontFamily: "var(--font-mono)" }}>{w.raw}</span>
                  {" · tabloda "}
                  <span style={{ fontFamily: "var(--font-mono)" }}>
                    {formatKpiNumber(w.tableValue)}
                  </span>
                </button>
                <button
                  type="button"
                  className="ml-auto shrink-0 text-xs underline"
                  style={{ color: "var(--color-text-3)" }}
                  title={C.ignoreTitle}
                  aria-label={`${C.ignore}: ${w.indicatorLabel} ${w.raw}`}
                  onClick={() => ignoreOne(w)}
                >
                  {C.ignore}
                </button>
              </li>
            ))}
            {visible.length > 12 && (
              <li className="text-xs" style={{ color: "var(--color-text-3)" }}>
                +{visible.length - 12} daha
              </li>
            )}
          </ul>
        </>
      )}
      <UnitNotesList notes={unitNotes} onLocate={onLocate} />
    </div>
  );
}

/** TODO-KPI-27 — nötr bilgi satırları: "Birim farkı · Toplam enerji tüketimi: metinde 800.596 MWh · tabloda 2.882.145,6 GJ — birim farklı (MWh ↔ GJ)". */
function UnitNotesList(p: {
  notes: KpiTextWarning[];
  onLocate?: ((w: KpiTextWarning) => void) | undefined;
}): JSX.Element | null {
  if (p.notes.length === 0) return null;
  return (
    <ul
      className="mt-2 flex flex-col gap-1 text-xs"
      data-testid="kpi-unit-notes"
      style={{ color: "var(--color-text-2)" }}
    >
      {p.notes.slice(0, 8).map((w, i) => (
        <li key={`${w.indicatorCode}-${w.sectionIndex}-${i}`}>
          <button
            type="button"
            className="text-left hover:underline"
            onClick={() => p.onLocate?.(w)}
            title="Metinde göster"
          >
            <span style={{ color: "var(--color-text-3)" }}>ℹ {C.unitNoteTitle} · </span>
            <span className="font-medium">{w.indicatorLabel}</span>
            {": metinde "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{w.raw}</span>
            {" · tabloda "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{formatKpiNumber(w.tableValue)}</span>
            {w.note ? ` — ${w.note}` : ""} {C.unitNoteSuffix}
          </button>
        </li>
      ))}
      {p.notes.length > 8 && (
        <li style={{ color: "var(--color-text-3)" }}>+{p.notes.length - 8} daha</li>
      )}
    </ul>
  );
}

/** Yalnız birim notu varsa: uyarı başlığı/amber zemin yok — nötr kutu. */
function UnitNotesOnly(p: {
  notes: KpiTextWarning[];
  onLocate?: ((w: KpiTextWarning) => void) | undefined;
}): JSX.Element {
  return (
    <div
      className="mb-3 rounded-md p-3 text-sm"
      role="status"
      style={{
        background: "var(--color-bg)",
        border: "1px solid var(--color-border)",
        color: "var(--color-text)",
      }}
    >
      <strong style={{ color: "var(--color-text-2)" }}>ℹ {C.unitNoteTitle}</strong>
      <UnitNotesList notes={p.notes} onLocate={p.onLocate} />
    </div>
  );
}
